import { cn } from "../utils/cn";
import { formatCurrency } from "../utils/formatters";
import { StarRating } from "./StarRating";
import { DifficultyBadge } from "./DifficultyBadge";
import type { Difficulty } from "./DifficultyBadge";

// ── Icons ─────────────────────────────────────────────────────────────────────

function LocationIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" className="shrink-0">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      aria-hidden="true" className="shrink-0">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function AltitudeIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" className="shrink-0">
      <path d="M12 2L2 19h20L12 2z" />
      <path d="M12 9v6" strokeWidth="1.5" />
    </svg>
  );
}

// ── Season config ─────────────────────────────────────────────────────────────

const SEASON_STYLE: Record<string, string> = {
  spring:  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  summer:  "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  autumn:  "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  winter:  "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  monsoon: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
};

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RouteCardProps {
  name: string;
  slug: string;
  region: string;
  difficulty: Difficulty;
  durationDays: number;
  maxAltitudeM?: number;
  priceFrom: number;
  totalOperators: number;
  avgRating: number;
  totalReviews: number;
  thumbnailUrl: string;
  bestSeasons: string[];
  isOpen: boolean;
  onClick?: () => void;
  className?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatAlt(m: number): string {
  return `${m.toLocaleString("en-IN")}m`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function RouteCard({
  name,
  region,
  difficulty,
  durationDays,
  maxAltitudeM,
  priceFrom,
  totalOperators,
  avgRating,
  totalReviews,
  thumbnailUrl,
  bestSeasons,
  isOpen,
  onClick,
  className,
}: RouteCardProps) {
  return (
    <article
      onClick={onClick}
      className={cn(
        "group relative flex flex-col overflow-hidden",
        "rounded-card bg-white dark:bg-surface-raised",
        "border border-border-default",
        "shadow-sm hover:shadow-md",
        "transition-all duration-200 ease-out",
        onClick && "cursor-pointer",
        className,
      )}
    >
      {/* ── Image ────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden aspect-video">
        <img
          src={thumbnailUrl}
          alt={name}
          loading="lazy"
          className={cn(
            "w-full h-full object-cover",
            "transition-transform duration-500 ease-out",
            "group-hover:scale-[1.03]",
          )}
        />

        {/* Difficulty badge — bottom-left overlay */}
        <div className="absolute bottom-3 left-3">
          <DifficultyBadge difficulty={difficulty} size="sm" showIcon />
        </div>

        {/* Trail closed banner — full-width at bottom */}
        {!isOpen && (
          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-1.5 py-1.5 bg-red-600/90 backdrop-blur-sm">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="3" strokeLinecap="round" />
            </svg>
            <span className="text-white text-[11px] font-bold uppercase tracking-widest">
              Trail Closed
            </span>
          </div>
        )}

        {/* Gradient fade at bottom for readability */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-16 pointer-events-none"
          style={{ background: "linear-gradient(to top, rgba(0,0,0,0.35) 0%, transparent 100%)" }}
        />
      </div>

      {/* ── Body ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 p-4 gap-2">
        {/* Name */}
        <h3 className="font-semibold text-base leading-snug text-content-primary line-clamp-1">
          {name}
        </h3>

        {/* Region */}
        <div className="flex items-center gap-1 text-content-secondary">
          <LocationIcon />
          <span className="text-[13px] truncate">{region}</span>
        </div>

        {/* Stats: duration + altitude */}
        <div className="flex items-center gap-4 text-[13px] text-content-secondary">
          <span className="flex items-center gap-1">
            <ClockIcon />
            {durationDays} day{durationDays !== 1 ? "s" : ""}
          </span>
          {maxAltitudeM && (
            <span className="flex items-center gap-1">
              <AltitudeIcon />
              {formatAlt(maxAltitudeM)}
            </span>
          )}
        </div>

        {/* Season chips */}
        {bestSeasons.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {bestSeasons.map((season) => (
              <span
                key={season}
                className={cn(
                  "px-2 py-0.5 text-[11px] font-medium rounded-full",
                  SEASON_STYLE[season.toLowerCase()] ??
                    "bg-surface-overlay text-content-secondary",
                )}
              >
                {season.charAt(0).toUpperCase() + season.slice(1)}
              </span>
            ))}
          </div>
        )}

        {/* ── Bottom row ─────────────────────────────────────────────── */}
        <div className="mt-auto pt-3 flex items-end justify-between border-t border-border-default">
          {/* Rating + review count */}
          <StarRating
            value={avgRating}
            count={totalReviews}
            size="sm"
          />

          {/* Price + operators */}
          <div className="text-right shrink-0 ml-2">
            <p className="text-sm font-bold text-brand-purple dark:text-violet-400 leading-tight">
              from {formatCurrency(priceFrom)}
            </p>
            <p className="text-[11px] text-content-tertiary leading-tight">
              · {totalOperators} operator{totalOperators !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}

RouteCard.displayName = "RouteCard";
